/**
 * 「按书录入」粘贴框里的**示例与格式说明**（纯字符串，单独一个模块）。
 *
 * ## 为什么把它们搬出组件
 *
 * 这些字符串是用户手上唯一的"格式说明书"，而它们**必须是解析器真认的格式**。
 * 2026-10-16 之前界面上写着"给字注音：写 `长 cháng` 或 `长:cháng`"，可是把
 * `长 cháng` 单独粘进"按书录入"的框里，解析器（`parseDictationImport`）报的是
 * 「「字」列必须是单个汉字，这里是 5 个字符：「cháng」」——
 * **用户照着界面上的例子抄，得到的是一个错误行**（实测见
 * `packages/shared/src/dictation/import-parser.ts` 的分隔符与列序规则）。
 *
 * 所以：要展示什么格式，就把**那条能真粘的文本**放在这里，
 * 由 `dictationListExamples.test.ts` 拿**真实解析器**逐条跑一遍。
 * 界面上的示例一旦与解析器脱钩，那个测试立刻失败。
 *
 * ⚠️ 别在这里写"看着好看但其实不合法"的例子。
 * ⚠️ 逗号是**半角逗号**；分列也可以用制表符（从 Excel 复制就是它）。
 */
import type { ListType } from '@fht/shared';

/**
 * 识字表 / 写字表粘贴框的占位示例（**只给人看**，不参与解析测试）。
 *
 * ⚠️ 2026-10-16 起规则变了（用户明确要求）：**识字表 / 写字表里只能录单个的汉字**，
 *    **词语表里只能录词语或成语**。所以这里**不能**再写 `天 天地`
 *    （那是老写法：顺手给字造词）—— 造词请走「浏览与编辑」里**点字 → 弹框**。
 *
 * ⚠️ 用数组 join 而不是模板字符串里的 `\n`：本项目见过"转义被吞掉之后
 *    示例变成一整行"的情况，而示例恰恰是家长唯一的格式说明书。
 */
export const HANZI_LIST_PLACEHOLDER = [
  '天 地 人 你 我 他',
  '（一行可以写多个，用空格或 Tab 分开都行；一行一个也认）',
  '',
  '口\t耳\t目\t手\t足',
  '（从 Excel 整列复制过来就是这样，Tab 也认）',
  '',
  '⚠️ 这里只能录单个的汉字：`天 天地` 里的「天地」会被报错。',
  '要给某个字造词/成语，保存后在「浏览与编辑」里点这个字，在弹出框里录。',
  '',
  '要指定读音，就整份都按两列写，第一行是表头：',
  '字,拼音',
  '长,cháng',
].join('\n');

/** 词语表粘贴框的占位示例（**只给人看**）。 */
export const WORD_LIST_PLACEHOLDER = [
  '天地 人口 耳目',
  '（一行可以写多个词，用空格或 Tab 分开都行）',
  '',
  '手足',
  '日月',
  '',
  '⚠️ 这里只能录词语或成语（2 个及以上汉字）：单个的字会被报错。',
  '生字请录到「识字表 / 写字表」。',
  '',
  '要指定读音，就整份都按两列写，第一行是表头：',
  '词,拼音',
  '长江,cháng jiāng',
].join('\n');

/** 字表：一行多个单字（空格 / Tab / 一行一个 都认）。 */
export const CHAR_RUN_EXAMPLE = '天 地 人';

/**
 * 字表：用 **Tab** 排开（从 Excel 复制出来的形状）。
 *
 * ⚠️ 这一条以前是 `天 天地`（"顺手造词"）—— 2026-10-16 起**不再允许**：
 *    字表里出现词语会被逐词元报错，造词改走「浏览 → 点字 → 弹框」。
 */
export const CHAR_TAB_EXAMPLE = ['天\t地\t人'].join('\n');

/** 词语表：一行多个词。 */
export const WORD_LIST_EXAMPLE = '天地 人口 耳目';

/**
 * 给字注音：**整份按两列写，第一行是表头**，列之间用逗号。
 *
 * ⚠️ 拼音里有空格（`cháng jiāng`），所以列之间**不能**用空格 ——
 *    空格分列会把多音节拼音切成好几格（实测会报"音节数与字数不一致"）。
 */
export const CHAR_PINYIN_EXAMPLE = ['字,拼音', '长,cháng'].join('\n');

/** 给词注音：同上，两列 + 表头。 */
export const WORD_PINYIN_EXAMPLE = ['词,拼音', '长江,cháng jiāng'].join('\n');

/**
 * **上传文件里**的写法（2026-10-20）：一行 = 课本上的一行，词元是 `字,拼音`，
 * 词元之间用 **Tab**（也认空格）。
 *
 * ⚠️ 用户锁定的文件格式就是这一条，**不要改**：助手按这个格式生成 .txt，
 *    家长上传后内容原样填进粘贴框。
 */
export const CHAR_FILE_PINYIN_EXAMPLE = '霜,shuāng\t吹,chuī\t落,luò\t降,jiàng';

/**
 * **文件里的「不确定」标记**（2026-10-20 第七轮）：拼音末尾一个 `?`。
 *
 * ```text
 * 得,dé?	背,bēi?	霜,shuāng
 * ```
 *
 *   · `得,dé?` / `背,bēi?` = 生成文件的人**不确定**这个读音 ⇒ 预览里标浅橙、
 *     等你点开确认（确认之后变浅绿）；
 *   · `霜,shuāng` = 确定的音，照旧；
 *   · 半角 `?` 与全角 `？` 都认；`?` **不是拼音的一部分**（解析前就剥掉）。
 *
 * ⚠️ 这一条也被 `dictationListExamples.test.ts` 拿**真实解析器**跑过：界面上的
 *    示例必须是解析器真认的格式（否则用户照抄会得到错误行）。
 */
export const CHAR_FILE_PINYIN_UNCERTAIN_EXAMPLE = '得,dé?\t背,bēi?\t霜,shuāng';

/** 词语表的文件写法：一个词元是 `词语,拼音`（词里有几个字就写几个音节）。 */
export const WORD_FILE_PINYIN_EXAMPLE = '长江,cháng jiāng\t黄河,huáng hé';

/** 一条"能直接粘"的示例 + 它应该被解析成什么（测试的断言口径）。 */
export interface ListFormatExample {
  id: string;
  /** 界面上那句话（`title` 后面接 `help`）。 */
  title: string;
  help: string;
  /** **能直接粘的示例正文**。 */
  text: string;
  /** 喂给解析器的默认表类型（"按书录入"是按表粘的）。 */
  listType: ListType;
  /** 这条示例应当解析出的东西（**测试断言用**，不是给人看的）。 */
  expect: {
    /** 解析出的字（按出现顺序）。 */
    chars: string[];
    /** 解析出的词（按出现顺序）。 */
    words: string[];
    /** 这些字/词上应该有的读音覆盖（`'长'` → `'cháng'`）。 */
    pinyin?: Record<string, string>;
    /** 这些字应当带着**「不确定」标记**（`?`；见 `ParsedImportRow.pinyinUncertain`）。 */
    uncertain?: string[];
  };
}

/**
 * 界面上列出来的全部示例。**每一条都必须能被解析器无错地认下来**
 * （见 `dictationListExamples.test.ts`）。
 */
export const LIST_FORMAT_EXAMPLES: readonly ListFormatExample[] = [
  {
    id: 'char-run',
    title: '字表（一行多个单字）',
    help: '一行可以写任意多个汉字，用空格或 Tab 分开；一行一个也认。从 Excel 整列复制过来就是 Tab。',
    text: CHAR_RUN_EXAMPLE,
    listType: 'shizi',
    expect: { chars: ['天', '地', '人'], words: [] },
  },
  {
    id: 'char-tab',
    title: '字表（Tab 分隔）',
    help: '从 Excel 复制出来是 Tab 分隔 —— 空格与 Tab 都认，一行里混用也认。',
    text: CHAR_TAB_EXAMPLE,
    listType: 'shizi',
    expect: { chars: ['天', '地', '人'], words: [] },
  },
  {
    id: 'char-pinyin',
    title: '给字注音',
    help: '整份都按两列写，第一行是表头，列之间用逗号（拼音里有空格，不能用空格分列）。',
    text: CHAR_PINYIN_EXAMPLE,
    listType: 'shizi',
    expect: { chars: ['长'], words: [], pinyin: { 长: 'cháng' } },
  },
  {
    id: 'word-list',
    title: '词语表（一行多个词）',
    help: '一行可以写多个词/成语，用空格或 Tab 分开；保存后会自动关联到每个词里的每一个字。',
    text: WORD_LIST_EXAMPLE,
    listType: 'cibiao',
    expect: { chars: [], words: ['天地', '人口', '耳目'] },
  },
  {
    id: 'word-pinyin',
    title: '给词注音',
    help: '多音字组成的词（例如「长江」「长大」）必须整词写拼音，否则会用这个字的默认读音。',
    text: WORD_PINYIN_EXAMPLE,
    listType: 'cibiao',
    expect: { chars: [], words: ['长江'], pinyin: { 长江: 'cháng jiāng' } },
  },
  {
    id: 'char-file-pinyin',
    title: '文件写法（字表：一行 = 课本一行）',
    help:
      '每个词元写「字,拼音」，词元之间用 Tab（也认空格）—— 上传的 .txt 就是这个写法。' +
      '单音字的读音一律以字典为准（文件里写错了也会按字典落库）；' +
      '凡是「文件里写的拼音和字典不一样」的位置，预览里会标浅橙、等你对着课本确认。',
    text: CHAR_FILE_PINYIN_EXAMPLE,
    listType: 'shizi',
    expect: {
      chars: ['霜', '吹', '落', '降'],
      words: [],
      pinyin: { 霜: 'shuāng', 吹: 'chuī', 落: 'luò', 降: 'jiàng' },
    },
  },
  {
    id: 'word-file-pinyin',
    title: '文件写法（词语表：一行 = 课本一行）',
    help: '词语表也一样：一个词元是「词语,拼音」，词里有几个字就写几个音节（中间用空格）。',
    text: WORD_FILE_PINYIN_EXAMPLE,
    listType: 'cibiao',
    expect: {
      chars: [],
      words: ['长江', '黄河'],
      pinyin: { 长江: 'cháng jiāng', 黄河: 'huáng hé' },
    },
  },
  {
    id: 'char-file-pinyin-uncertain',
    title: '文件写法：不确定的读音在末尾标一个「?」',
    help:
      '「字,拼音?」= 生成文件的人不确定这个读音：预览里这些字标浅橙色，' +
      '点它 → 浮窗里点「确认就是「dé」」（音对）或换一个读音（音不对），确认之后变绿；' +
      '带「?」还没确认的字不能保存。半角 ? 与全角 ？ 都认，? 不是拼音的一部分。',
    text: CHAR_FILE_PINYIN_UNCERTAIN_EXAMPLE,
    listType: 'shizi',
    expect: {
      chars: ['得', '背', '霜'],
      words: [],
      pinyin: { 得: 'dé', 背: 'bēi', 霜: 'shuāng' },
      uncertain: ['得', '背'],
    },
  },
];

/** 按 id 取一条示例（取不到时返回 `null`，**界面不要因此崩**）。 */
export function findListFormatExample(id: string): ListFormatExample | null {
  return LIST_FORMAT_EXAMPLES.find((example) => example.id === id) ?? null;
}
